import { isColor } from '../cards';
import { isSquare } from '../board';
import type { CardSpec, MatchConfig, PawnSpec } from '../types';
import {
  APPRAISAL_TARGETS,
  CARVINGS,
  FINISHES,
  HIDDEN_PATTERNS,
  HOUSE_RULES,
  OMENS,
  STAMPS,
  TRICKS,
  SUPERSTITIONS,
  TABLE_CURIOS,
  includes,
} from './ids';
import type { ItemSpec, KitSpec } from './types';

/**
 * Validation of an untrusted Long Night config (a stored run, an imported replay). Returns the
 * problems; empty when the ledger can be played.
 */

const MAX_ITEMS = 40;
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isNonNegInt = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0;

function itemProblems(list: unknown, what: string, ids: readonly string[]): string[] {
  if (!Array.isArray(list) || list.length > MAX_ITEMS) return [`${what} must be a short list`];
  const out: string[] = [];
  const uids = new Set<string>();
  for (const raw of list as unknown[]) {
    const i = raw as Partial<ItemSpec> | null;
    if (typeof i !== 'object' || i === null || typeof i.uid !== 'string' || i.uid.length === 0) {
      out.push(`${what} holds an invalid item`);
      continue;
    }
    if (uids.has(i.uid)) out.push(`${what}: duplicate item ${i.uid}`);
    uids.add(i.uid);
    if (!includes(ids, i.id)) out.push(`${what}: unknown item ${String(i.id)}`);
    if (i.finish !== undefined && !includes(FINISHES, i.finish)) out.push(`${what}: bad finish`);
    if (i.value !== undefined && (!isNum(i.value) || i.value < 0 || i.value > 1e6))
      out.push(`${what}: bad value`);
  }
  return out;
}

function kitProblems(kit: unknown, side: string): string[] {
  if (typeof kit !== 'object' || kit === null) return [`kit ${side} is missing`];
  const k = kit as Partial<KitSpec>;
  const out = [
    ...itemProblems(k.mantel, `kit ${side} Mantel`, SUPERSTITIONS),
    ...itemProblems(k.curios, `kit ${side} Curios`, TABLE_CURIOS),
  ];
  if (typeof k.appraisals !== 'object' || k.appraisals === null)
    out.push(`kit ${side}: appraisals`);
  else
    for (const [t, lvl] of Object.entries(k.appraisals))
      if (!includes(APPRAISAL_TARGETS, t) || !isNonNegInt(lvl) || lvl > 1000)
        out.push(`kit ${side}: bad appraisal ${t}`);
  if (!Array.isArray(k.discovered) || !k.discovered.every((p) => includes(HIDDEN_PATTERNS, p)))
    out.push(`kit ${side}: discovered patterns`);
  if (!isNum(k.brass) || Math.abs(k.brass) > 1e12) out.push(`kit ${side}: brass`);
  if (
    typeof k.counters !== 'object' ||
    k.counters === null ||
    !Object.values(k.counters).every(isNonNegInt)
  )
    out.push(`kit ${side}: counters`);
  if (typeof k.bonuses !== 'object' || k.bonuses === null) out.push(`kit ${side}: bonuses`);
  else
    for (const v of Object.values(k.bonuses))
      if (!isNonNegInt(v) || v > 10) out.push(`kit ${side}: bad bonus`);
  if (k.slots !== undefined && (!isNonNegInt(k.slots) || k.slots > 12))
    out.push(`kit ${side}: slots`);
  return out;
}

export function ledgerProblems(config: MatchConfig): string[] {
  const l = config.ledger;
  if (typeof l !== 'object' || l === null) return ['ledger must be an object'];
  const out: string[] = [];
  if (typeof l.kits !== 'object' || l.kits === null) out.push('ledger kits missing');
  else out.push(...kitProblems(l.kits.S, 'S'), ...kitProblems(l.kits.N, 'N'));
  if (!Array.isArray(l.omens) || l.omens.length > 24) out.push('ledger omens must be a short list');
  else {
    const seen = new Set<string>();
    for (const o of l.omens) {
      if (typeof o !== 'object' || o === null || !includes(OMENS, o.kind) || !isSquare(o.square)) {
        out.push('ledger holds an invalid Omen');
        continue;
      }
      if (o.square.y === 0 || o.square.y === 5) {
        if (o.kind !== 'altar') out.push('Omens other than Altars stay off the home rows');
      } else if (o.kind === 'altar') out.push('Altars stand on the back rows');
      const key = `${o.square.x},${o.square.y}`;
      if (seen.has(key)) out.push('two Omens on one square');
      seen.add(key);
    }
  }
  if (!Array.isArray(l.houseRules) || l.houseRules.length > 12)
    out.push('house rules must be a short list');
  else
    for (const r of l.houseRules) {
      if (typeof r !== 'object' || r === null || !includes(HOUSE_RULES, r.id))
        out.push('ledger holds an invalid House Rule');
      else {
        if (r.side !== undefined && r.side !== 'S' && r.side !== 'N')
          out.push('bad House Rule side');
        if (r.color !== undefined && !isColor(r.color)) out.push('bad House Rule color');
        if (r.n !== undefined && (!isNonNegInt(r.n) || r.n > 100))
          out.push('bad House Rule number');
      }
    }
  if (typeof l.anteBlind !== 'boolean') out.push('anteBlind must be true or false');
  if (l.startEpitaphs !== undefined) {
    const ids = new Set<string>();
    for (const side of ['S', 'N'] as const) {
      const list = l.startEpitaphs[side] ?? [];
      if (!Array.isArray(list) || list.length > 5) {
        out.push(`start Epitaphs ${side} must be a short list`);
        continue;
      }
      for (const e of list) {
        const links = e?.inscription?.links;
        if (
          typeof e?.id !== 'string' ||
          e.id.startsWith('EP') ||
          ids.has(e.id) ||
          !Array.isArray(links) ||
          links.length < 1 ||
          links.length > 4
        )
          out.push(`start Epitaphs ${side} hold an invalid Epitaph`);
        else ids.add(e.id);
      }
    }
  }
  for (const side of ['S', 'N'] as const) {
    for (const spec of (config.decks?.[side] ?? []) as readonly CardSpec[]) {
      if (spec.stamp !== undefined && !includes(STAMPS, spec.stamp))
        out.push(`deck ${side}: bad stamp`);
      if (spec.finish !== undefined && !includes(FINISHES, spec.finish))
        out.push(`deck ${side}: bad finish`);
      if (spec.value !== undefined && (!isNonNegInt(spec.value) || spec.value > 1e6))
        out.push(`deck ${side}: bad card value`);
      if (spec.trick !== undefined && (!includes(TRICKS, spec.trick) || spec.kind !== 'normal'))
        out.push(`deck ${side}: a Trick is a colored card with a known Trick`);
    }
    for (const p of (config.pawns?.[side] ?? []) as readonly PawnSpec[])
      if (p.carvings !== undefined) {
        if (!Array.isArray(p.carvings) || p.carvings.length > 4) out.push(`pawn ${p.id}: carvings`);
        else
          for (const c of p.carvings)
            if (
              !includes(CARVINGS, c?.id) ||
              (c.value !== undefined && (!isNonNegInt(c.value) || c.value > 1e6))
            )
              out.push(`pawn ${p.id}: bad carving`);
      }
  }
  return out;
}
